import React, { useState } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import isEqual from 'lodash.isequal';
import { format } from 'next/lib/date';
import { easing } from './lib/easing';
import { useDateRange, DATE_FORMAT } from './lib/date-range';
import Button, { ButtonGroup } from './Button';
import DateRangePicker from './DateRangePicker';
import Dropdown from './Dropdown';
import Icon from './Icon';

const Wrapper = styled.div``;

const Menu = styled.div`
  padding: 18px 24px;

  ${ButtonGroup} {
    margin-top: 18px;
  }
`;

const Trigger = styled(Button)`
  align-items: center;
  border: 1px solid var(--dropdown-inactive);
  color: var(--dropdown-text);
  display: flex;
  justify-content: space-between;
  text-transform: none;
  transition: ${easing('border')};

  :hover:not([disabled]) {
    border: 1px solid var(--dropdown-active);
    box-shadow: none;
  }

  ${Icon} {
    color: var(--dropdown-icon);
    font-size: var(--xx-small);
    margin-left: 16px;
    margin-right: 0;
  }

  ${({ active }) =>
    active &&
    `
      border: 1px solid var(--dropdown-active);
    `}
`;

const DateRangeLabel = styled.span`
  font-weight: var(--normal);
  line-height: 1;
`;

/**
 * Format start and end date label if both exist
 *
 * @param {DateRange} value - Date range to format
 * @param {Date} value.start - Start date
 * @param {Date} value.end - End date
 * @return {string} Formatted start and end date range label
 */
const formatDateRangeLabel = ({ start, end }) =>
  start && end
    ? `${format(start, DATE_FORMAT)} - ${format(end, DATE_FORMAT)}`
    : 'Select date range...';

/**
 * Dropdown menu component for date range picker and buttons
 */
const DateRangeMenu = ({
  error,
  onApply,
  onChange,
  onClose,
  value: initial = {},
  ...rest
}) => {
  const [value, setValue] = useState(initial);

  const handleApply = () => {
    onApply?.(value);
    onClose();
  };

  const handleChange = next => {
    setValue(next);
    onChange?.(next);
  };

  const { start, end } = value;
  const disabled = error || !(start && end) || isEqual(initial, value);

  return (
    <Menu>
      <DateRangePicker {...rest} onChange={handleChange} value={value} />

      <ButtonGroup right>
        <Button disabled={disabled} kind="positive" onClick={handleApply}>
          Apply
        </Button>
        <Button kind="secondary" onClick={onClose}>
          Cancel
        </Button>
      </ButtonGroup>
    </Menu>
  );
};

DateRangeMenu.propTypes = {
  ...DateRangePicker.propTypes,
  error: PropTypes.bool,
  onApply: PropTypes.func,
  onClose: PropTypes.func,
};

/**
 * Dropdown component for date range trigger button and menu attachment
 */
function DateRangeDropdown({
  className,
  disabled = false,
  error,
  onApply,
  options,
  value: initial = {},
  ...rest
}) {
  const [value, setValue] = useDateRange(options, initial);

  const handleApply = next => {
    setValue(next);
    onApply?.(next);
  };

  const attachment = ({ onClose }) => (
    <DateRangeMenu
      {...rest}
      disabled={disabled}
      error={error}
      onApply={handleApply}
      onClose={onClose}
      options={options}
      value={value}
    />
  );

  return (
    <Wrapper className={className}>
      <Dropdown
        attachment={attachment}
        closeOnEscape={false}
        disabled={disabled}
        placement="left"
      >
        {({ visible }) => (
          <Trigger
            aria-label="Open date range picker"
            active={visible}
            disabled={disabled}
            kind="secondary"
          >
            <DateRangeLabel>{formatDateRangeLabel(value)}</DateRangeLabel>
            <Icon icon={`chevron-${visible ? 'up' : 'down'}`} />
          </Trigger>
        )}
      </Dropdown>
    </Wrapper>
  );
}

DateRangeDropdown.propTypes = {
  ...DateRangePicker.propTypes,
  disabled: PropTypes.bool,
  error: PropTypes.bool,
  onApply: PropTypes.func,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string,
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    })
  ),
};

export default styled(DateRangeDropdown)``;
